/**
 * Empire Pro — custom search typeahead against Boost's public suggest endpoint.
 *
 * Replaces Boost's instant-search widget binding (boost-pfs-core-instant-search.js
 * on non-filter pages / initSearchBox on filter pages), which had an unfixable
 * first-interaction failure. We own binding + render; endpoint contract, caps,
 * and the analytics payloads are documented in search-typeahead-plan.md.
 *
 * PRIORITY ORDER (design invariant): the search bar submitting to /search must
 * work even if everything in this file fails. The component therefore NEVER
 * calls preventDefault on Enter unless a suggestion is actively highlighted —
 * plain Enter always falls through to native form submit.
 *
 * Analytics parity with the retired Boost widget:
 *  - suggest calls carry sid + event_type so Boost's query-stream learning
 *    (AI Synonyms etc.) sees us exactly like its own widget;
 *  - suggestion clicks beacon {u:'click_view', a:'suggest'} to the analytics
 *    endpoint;
 *  - PDP add-to-cart beacons {u:'add_to_cart'} (previously sent by the bundle's
 *    initAnalytics, which no longer runs on non-filter pages).
 */
(function () {
  'use strict';

  // The header and modal snippets each render this script tag; only the first
  // execution may attach module-level listeners (else the PDP ATC beacon dupes).
  if (window.__epSearchTypeahead) return;
  window.__epSearchTypeahead = true;

  var SUGGEST_URL = 'https://services.mybcapps.com/bc-sf-filter/search/suggest';
  var ANALYTICS_URL = 'https://lambda.mybcapps.com/e';
  var SID_KEY = 'boostPFSSessionId'; // shared with Boost so sessions stay continuous
  var DEBOUNCE_MS = 200;
  var MIN_CHARS = 2;
  var MAX_PRODUCTS = 6;
  var MAX_SUGGESTIONS = 3;
  var CACHE_MAX = 25;

  /* ------------------------------------------------------------------ utils */

  function lsGet(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }
  function lsSet(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  function getSessionId() {
    // Boost stores the id JSON-serialized ('"uuid"') and transmits it bare —
    // parse on read and store serialized, or our events land under a
    // quote-wrapped sid that never joins Boost's session.
    var sid = lsGet(SID_KEY);
    if (sid) {
      try {
        var parsed = JSON.parse(sid);
        if (typeof parsed === 'string') sid = parsed;
      } catch (e) { /* already bare */ }
      return sid;
    }
    sid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 3) | 8).toString(16);
    });
    lsSet(SID_KEY, JSON.stringify(sid));
    return sid;
  }

  // Cart token for analytics attribution ('' = empty cart, per Boost's contract).
  var cartTokenPromise = null;
  function getCartToken() {
    if (!cartTokenPromise) {
      cartTokenPromise = fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) { return cart.item_count > 0 ? cart.token : ''; })
        .catch(function () { return ''; });
    }
    return cartTokenPromise;
  }

  function sendAnalytics(event) {
    event.sid = getSessionId();
    try {
      // Not sendBeacon: it always sends credentials, and lambda answers
      // ACAO:* which rejects credentialed requests (hard CORS failure in
      // Firefox). Boost's own bundle sends an uncredentialed XHR — mirror it.
      fetch(ANALYTICS_URL, {
        method: 'POST',
        body: JSON.stringify(event),
        headers: { 'Content-Type': 'application/json;charset=UTF-8' },
        credentials: 'omit',
        keepalive: true,
      }).catch(function () {});
    } catch (e) { /* analytics must never break UX */ }
  }

  // Bold the query tokens inside a title. DOM-built (no innerHTML of API data).
  function highlight(text, query) {
    var frag = document.createDocumentFragment();
    var tokens = query.trim().toLowerCase().split(/\s+/).filter(function (t) { return t.length >= 2; });
    if (!tokens.length) {
      frag.appendChild(document.createTextNode(text));
      return frag;
    }
    var pattern = tokens
      .map(function (t) { return t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); })
      .join('|');
    var re = new RegExp('(' + pattern + ')', 'ig');
    var last = 0;
    var m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
      var b = document.createElement('b');
      b.textContent = m[0];
      frag.appendChild(b);
      last = m.index + m[0].length;
    }
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
    return frag;
  }

  function metafieldValue(product, key) {
    var list = product.metafields;
    if (!Array.isArray(list)) return '';
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].namespace === 'custom' && list[i].key === key) return String(list[i].value || '');
    }
    return '';
  }

  /* -------------------------------------------------------------- component */

  var cache = new Map(); // term -> response (shared across instances)

  function cacheSet(term, data) {
    if (cache.size >= CACHE_MAX) cache.clear();
    cache.set(term, data);
  }

  var EpSearchTypeahead = function () {
    return Reflect.construct(HTMLElement, [], EpSearchTypeahead);
  };
  EpSearchTypeahead.prototype = Object.create(HTMLElement.prototype);
  EpSearchTypeahead.prototype.constructor = EpSearchTypeahead;

  EpSearchTypeahead.prototype.connectedCallback = function () {
    var self = this;
    this._shop = this.getAttribute('data-shop') || '';
    this._searchUrl = this.getAttribute('data-search-url') || '/search';
    this._input = this.querySelector('input[name="q"]');
    if (!this._input || !this._shop) return; // bar keeps working as a plain form

    this._panel = document.createElement('div');
    this._panel.className = 'ep-typeahead__panel';
    this._panel.id = 'ep-typeahead-panel-' + (this._input.id || Math.random().toString(36).slice(2));
    this._panel.setAttribute('role', 'listbox');
    this._panel.hidden = true;
    this.appendChild(this._panel);

    this._input.setAttribute('role', 'combobox');
    this._input.setAttribute('aria-expanded', 'false');
    this._input.setAttribute('aria-controls', this._panel.id);
    this._input.setAttribute('aria-autocomplete', 'list');

    this._activeIndex = -1;
    this._debounceTimer = null;
    this._abort = null;

    this._onInput = function () { self._scheduleQuery(); };
    this._onKeydown = function (e) { self._handleKeydown(e); };
    this._onFocus = function () {
      var term = self._input.value.trim();
      if (term.length >= MIN_CHARS && cache.has(term.toLowerCase())) {
        self._render(cache.get(term.toLowerCase()), term);
      }
    };
    this._onDocPointerDown = function (e) {
      if (!self.contains(e.target)) self._close();
    };

    this._input.addEventListener('input', this._onInput);
    this._input.addEventListener('keydown', this._onKeydown);
    this._input.addEventListener('focus', this._onFocus);
    document.addEventListener('pointerdown', this._onDocPointerDown);
  };

  EpSearchTypeahead.prototype.disconnectedCallback = function () {
    document.removeEventListener('pointerdown', this._onDocPointerDown);
    if (this._abort) this._abort.abort();
    clearTimeout(this._debounceTimer);
  };

  EpSearchTypeahead.prototype._scheduleQuery = function () {
    var self = this;
    clearTimeout(this._debounceTimer);
    var term = this._input.value.trim();
    if (term.length < MIN_CHARS) {
      this._close();
      return;
    }
    this._debounceTimer = setTimeout(function () { self._query(term); }, DEBOUNCE_MS);
  };

  EpSearchTypeahead.prototype._query = function (term) {
    var self = this;
    var key = term.toLowerCase();
    if (cache.has(key)) {
      this._render(cache.get(key), term);
      return;
    }
    if (this._abort) this._abort.abort();
    this._abort = new AbortController();
    var params = new URLSearchParams({
      q: term,
      shop: this._shop,
      t: String(Date.now()),
      event_type: 'suggest',
      sid: getSessionId(),
      product_limit: String(MAX_PRODUCTS),
      suggestion_limit: String(MAX_SUGGESTIONS),
      collection_limit: '0',
      page_limit: '0',
      dym_limit: '0',
    });
    fetch(SUGGEST_URL + '?' + params.toString(), { signal: this._abort.signal })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        cacheSet(key, data);
        // stale guard: only render if the input still shows this term
        if (self._input.value.trim().toLowerCase() === key) self._render(data, term);
      })
      .catch(function () { /* aborted or offline — bar still submits natively */ });
  };

  // Horizon re-renders header sections by DOM-morphing them against server
  // markup, which strips our injected panel (it keeps the input node, so
  // listeners survive — but renders went into a detached element). Self-heal:
  // re-append the panel (and re-assert the input's ARIA wiring, which a morph
  // can also strip) whenever we're about to show it.
  EpSearchTypeahead.prototype._ensurePanel = function () {
    if (!this._panel.isConnected) this.appendChild(this._panel);
    if (this._input.getAttribute('aria-controls') !== this._panel.id) {
      this._input.setAttribute('role', 'combobox');
      this._input.setAttribute('aria-controls', this._panel.id);
      this._input.setAttribute('aria-autocomplete', 'list');
    }
  };

  EpSearchTypeahead.prototype._render = function (data, term) {
    var self = this;
    this._ensurePanel();
    var products = (data.products || []).slice(0, MAX_PRODUCTS);
    // Skip suggestions that add nothing over the typed query (Boost sometimes
    // suggests the query itself or a doubled token like "jbl jbl").
    var qNorm = term.trim().toLowerCase().replace(/\s+/g, ' ');
    var suggestions = (data.suggestions || [])
      .filter(function (s) {
        var tokens = String(s).toLowerCase().trim().split(/\s+/);
        var deduped = tokens.filter(function (t, i) { return t !== tokens[i - 1]; }).join(' ');
        return deduped !== qNorm;
      })
      .slice(0, MAX_SUGGESTIONS);
    this._panel.textContent = '';
    this._activeIndex = -1;

    if (!products.length && !suggestions.length) {
      this._close();
      return;
    }

    if (suggestions.length) {
      var pillRow = document.createElement('div');
      pillRow.className = 'ep-typeahead__pills';
      suggestions.forEach(function (s) {
        var a = document.createElement('a');
        a.className = 'ep-typeahead__pill';
        a.setAttribute('role', 'option');
        a.href = self._searchUrl + '?q=' + encodeURIComponent(s) + '&options%5Bprefix%5D=last';
        a.appendChild(highlight(s, term));
        pillRow.appendChild(a);
      });
      this._panel.appendChild(pillRow);
    }

    products.forEach(function (p) {
      var a = document.createElement('a');
      a.className = 'ep-typeahead__item';
      a.setAttribute('role', 'option');
      a.setAttribute('data-id', String(p.id));
      a.href = '/products/' + p.handle;

      var title = document.createElement('span');
      title.className = 'ep-typeahead__item-title';
      title.appendChild(highlight(String(p.title || ''), term));
      a.appendChild(title);

      var desc = metafieldValue(p, 'short_description');
      if (desc) {
        var d = document.createElement('span');
        d.className = 'ep-typeahead__item-desc';
        d.textContent = desc;
        a.appendChild(d);
      }

      a.addEventListener('click', function () {
        self._beaconProductClick(p, term);
      });
      self._panel.appendChild(a);
    });

    this._panel.hidden = false;
    this._input.setAttribute('aria-expanded', 'true');
  };

  EpSearchTypeahead.prototype._beaconProductClick = function (product, term) {
    var self = this;
    getCartToken().then(function (ct) {
      sendAnalytics({
        tid: self._shop,
        ct: ct,
        pid: String(product.id),
        t: new Date().toISOString(),
        u: 'click_view',
        a: 'suggest',
        qs: 'q=' + encodeURIComponent(term),
        r: document.referrer,
      });
    });
  };

  EpSearchTypeahead.prototype._close = function () {
    this._panel.hidden = true;
    this._panel.textContent = '';
    this._activeIndex = -1;
    this._input.setAttribute('aria-expanded', 'false');
    this._input.removeAttribute('aria-activedescendant');
  };

  EpSearchTypeahead.prototype._options = function () {
    return Array.prototype.slice.call(this._panel.querySelectorAll('[role="option"]'));
  };

  EpSearchTypeahead.prototype._handleKeydown = function (e) {
    var options = this._panel.hidden ? [] : this._options();

    if (e.key === 'Escape') {
      if (!this._panel.hidden) {
        this._close();
        // Panel first, dialog second: stopPropagation alone can't hold a native
        // <dialog> open — its Escape-cancel only yields to preventDefault.
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!options.length) return;
      e.preventDefault();
      var delta = e.key === 'ArrowDown' ? 1 : -1;
      // Cycle through [-1, 0, .. length-1]: shift into 0-based space (+1),
      // step, wrap over length+1 states, shift back.
      this._activeIndex = (this._activeIndex + 1 + delta + options.length + 1) % (options.length + 1) - 1;
      var self = this;
      options.forEach(function (el, i) {
        el.classList.toggle('is-active', i === self._activeIndex);
        if (i === self._activeIndex) {
          el.id = el.id || self._panel.id + '-opt-' + i;
          self._input.setAttribute('aria-activedescendant', el.id);
          el.scrollIntoView({ block: 'nearest' });
        }
      });
      if (this._activeIndex === -1) this._input.removeAttribute('aria-activedescendant');
      return;
    }

    if (e.key === 'Enter') {
      // THE invariant: only intercept Enter when a suggestion is actively
      // highlighted. Otherwise the event falls through untouched and the form
      // submits natively — the bar can never look dead.
      var active = this._activeIndex >= 0 ? options[this._activeIndex] : null;
      if (active) {
        e.preventDefault();
        // click() both fires the analytics handler and navigates the anchor —
        // do NOT also location.assign(): the second racing navigation crashes
        // WebKit (reproduced twice in the Playwright bench, 2026-08-28).
        active.click();
      }
    }
  };

  if (!customElements.get('ep-search-typeahead')) {
    customElements.define('ep-search-typeahead', EpSearchTypeahead);
  }

  /* ---------------------------------------------- PDP add-to-cart analytics */
  // The Boost bundle's initAnalytics no longer runs on non-filter pages, so its
  // product-page ATC event (revenue attribution) is replicated here. Payload
  // matches the bundle's O() function: {tid, pid, u, ct}.
  var pdpProductId =
    window.boostPFSConfig &&
    window.boostPFSConfig.general &&
    window.boostPFSConfig.general.product_id;
  if (pdpProductId) {
    document.addEventListener(
      'click',
      function (e) {
        var t = e.target;
        if (!t || typeof t.closest !== 'function') return;
        if (!t.closest('form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"] [name="add"]')) return;
        getCartToken().then(function (ct) {
          sendAnalytics({
            tid: (window.boostPFSConfig.shop && window.boostPFSConfig.shop.domain) || '',
            pid: String(pdpProductId),
            u: 'add_to_cart',
            ct: ct,
          });
        });
      },
      true
    );
  }
})();
